import { useMemo, useState } from 'react';

import { Draggable } from '@mint-lib/icons';
import { Card, Checkbox, TruncateText } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type { FileNode, FileSystemNode } from '../../../../types/fileSystem.js';
import type { RagTableType } from '../../../../types/rag.js';
import { ActionsCell } from '../../RagTable/cells/ActionsCell.jsx';
import { useNodeDragSource } from '../dnd/dndContext.js';
import { FileIcon } from '../FileIcon/FileIcon.jsx';
import { TileRename } from './TileRename.jsx';

const FileSystemTile = ({
  item,
  isSelected,
  onToggleSelect,
  onOpenDeleteDialog,
  onUnselectTableRows,
  onOpenEditPermissionsDialog,
  onOpenMoveDialog,
}: {
  item: RagTableType;
  isSelected: boolean;
  onToggleSelect: (file: RagTableType) => void;
  onUnselectTableRows: () => void;
  onOpenDeleteDialog: (rows: RagTableType[]) => void;
  onOpenEditPermissionsDialog: (document: RagTableType) => void;
  onOpenMoveDialog: (nodes: FileSystemNode[]) => void;
}) => {
  const [fileName, setFileName] = useState(item.fileName);
  const [originalFileName, setOriginalFileName] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const node = useMemo<FileNode>(
    () => ({ kind: 'file', id: item.fileId, ...item }),
    [item],
  );
  const { setDragRef, dragHandleProps, isDragging } = useNodeDragSource(node);

  const handleRenameStart = () => {
    setOriginalFileName(fileName);
    setIsEditing(true);
  };

  const handleRenameCancel = () => {
    setFileName(originalFileName);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <TileRename
        item={item}
        fileName={fileName}
        setIsEditing={setIsEditing}
        setFileName={setFileName}

        handleRenameCancel={handleRenameCancel}
      />
    );
  }

  return (
    <Card
      ref={setDragRef}
      variant={'outlined'}
      className={cn('w-full', isDragging && 'opacity-40')}
      contentClassName="flex flex-row gap-2 items-center justify-start p-2"
    >
      <Draggable
        {...dragHandleProps}
        data-dd-action-name="Knowledge base: drag file"
        className="shrink-0 cursor-grab fill-icon-secondary active:cursor-grabbing"
      />

      <Checkbox
        checked={isSelected}
        aria-label={`select-file-${item.fileName}`}
        onCheckedChange={() => onToggleSelect(item)}
      />
      <FileIcon type={item.fileType} />
      <TruncateText>{fileName}</TruncateText>

      <ActionsCell
        onOpenMoveDialog={() =>
          onOpenMoveDialog([{ ...item, kind: 'file', id: item.fileId }])
        }
        onOpenDeleteDialog={onOpenDeleteDialog}
        onUnselectTableRows={onUnselectTableRows}
        onOpenEditPermissionsDialog={onOpenEditPermissionsDialog}
        onRename={handleRenameStart}
        data={item}
        className="ml-auto"
      />
    </Card>
  );
};

export { FileSystemTile };
